// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid2,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
import { JobCostingItem } from '~/components/module/job';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { EMPTY_STRING } from '~/utils/constants/common';
import { formatCurrencyUSD, formatInputCurrency } from '~/utils/helper/common';
import { defaultValuesForm, schema } from './helper';
import { TypeJobDetailEnum } from '~/utils/enum/common';
import { additionalExpense, getDetailJob } from '~/thunks/job/jobThunk';
import { IFormOrderCosting, IOrderCostingDetail, IParamQueryOrderDetail } from '~/utils/interface/order';
import { IBodyUpdate } from '~/utils/interface/common';
// Styles, images, icons
import styles from './OrderCostingTab.module.scss';
import './OrderCostingTab.scss';
import { icons } from '~/assets';
import { getOrderDetail } from '~/thunks/order/orderThunk';

type Props = {};

const cx = classNames.bind(styles);

const OrderCostingTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IFormOrderCosting>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const {
    fields: additionalExpensesFields,
    append: appendAdditionalExpense,
    remove: removeAdditionalExpense,
  } = useFieldArray({
    control,
    name: 'additionalExpenses',
  });
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshJobDetail } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderCostingDetail, setOrderCostingDetail] = useState<IOrderCostingDetail>();
  //#endregion Declare State

  //#region Declare Memo
  const dataTime = useMemo(() => {
    return [
      {
        title: t('order_detail_title_projected_time'),
        value: orderCostingDetail?.loadingTime,
        subTitle: t('order_detail_title_loading_time'),
      },
      {
        title: t('order_detail_title_delivery_time'),
        value: orderCostingDetail?.deliveryTime,
        subTitle: t('order_detail_title_hours_for_delivery'),
      },
      {
        title: t('order_detail_title_pickup_time'),
        value: orderCostingDetail?.pickupTime,
        subTitle: t('order_detail_title_hours_for_pickup'),
      },
      {
        title: t('order_detail_title_unloading_time'),
        value: orderCostingDetail?.unLoadingTime,
        subTitle: t('order_detail_title_hours_for_unloading'),
      },
      {
        title: t('order_detail_title_total_projected_time'),
        value: orderCostingDetail?.totalTime,
        subTitle: t('order_detail_title_hours_for_order'),
      },
    ];
  }, [orderCostingDetail]);

  const dataOverview = useMemo(() => {
    return [
      {
        title: t('order_detail_title_order_revenue'),
        value: orderCostingDetail?.revenue?.totalAmount,
        subTitle: t('order_detail_title_total_revenue'),
      },
      {
        title: t('order_detail_title_order_expenses'),
        value: orderCostingDetail?.expenses?.totalAmount,
        subTitle: t('order_detail_title_total_expenses'),
      },
      {
        title: t('order_detail_title_profit_margin'),
        value: orderCostingDetail?.profitMargin ? `${orderCostingDetail?.profitMargin}%` : EMPTY_STRING,
        isWarning: Number(orderCostingDetail?.profitMargin) < 70,
        subTitle: t('order_detail_title_profit_margin_percentage'),
      },
    ];
  }, [orderCostingDetail]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!orderCostingDetail) return;
    const newData: IFormOrderCosting = {
      additionalExpenses: orderCostingDetail?.expenses?.additionalExpenses.map((item) => ({
        ...item,
        idDetail: item.id,
      })),
    };

    reset(newData);
  }, [orderCostingDetail]);

  useEffect(() => {
    if (!orderId) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId,
      type: TypeJobDetailEnum.COSTING,
    };

    handleGetDetailJob(params);
  }, [orderId]);

  useEffect(() => {
    if (!isRefreshJobDetail || !orderId) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId,
      type: TypeJobDetailEnum.COSTING,
    };

    handleGetDetailJob(params);
  }, [isRefreshJobDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSave = (data: IFormOrderCosting) => {
    if (!orderId) return;

    setIsLoading(true);

    const newData = {
      ...data,
      additionalExpenses: data?.additionalExpenses?.map(({ id, idDetail, ...item }) => ({
        ...item,
        id: idDetail,
      })),
    };

    const payload: IBodyUpdate<IFormOrderCosting> = {
      id: orderId,
      body: newData,
    };

    dispatch(additionalExpense(payload))
      .unwrap()
      .then(() => {})
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleAddAdditionalExpense = () => {
    appendAdditionalExpense({
      description: EMPTY_STRING,
      amount: EMPTY_STRING,
    });
  };

  const handleRemoveAdditionalExpense = (index: number, id?: string) => {
    if (id) {
      setValue('deleteAdditionalExpenseIds', [...(getValues('deleteAdditionalExpenseIds') || []), id]);
    }

    removeAdditionalExpense(index);
  };

  const handleGetDetailJob = (params: IParamQueryOrderDetail) => {
    setIsLoading(true);

    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setOrderCostingDetail(res.data as IOrderCostingDetail);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function
  return (
    <Box id='infoJobTabComponent' component={'form'} onSubmit={handleSubmit(handleSave)} className={cx('container')}>
      <Stack direction='column' gap={5} className={cx('viewBoxTime')}>
        <Stack className={cx('viewHeader')}>
          <Typography className={cx('title')}>{t('order_detail_title_costing')}</Typography>
        </Stack>

        <Stack direction='row' alignItems='center' gap={6} className={cx('viewStatistic')}>
          {dataTime && dataTime.map((item) => <JobCostingItem data={item} />)}
        </Stack>

        <Stack direction='column' alignItems='start' gap={2} className={cx('alert')}>
          <Stack direction='row' gap={2}>
            <img
              src={icons.commonIconWarningError}
              alt={t('common_alt_img', { ns: 'common/base' })}
              width={20}
              height={20}
            />
            <Typography className={cx('alertTitle')}>{t('order_detail_costing_alert_title')}</Typography>
          </Stack>
          <Typography className={cx('alertSubTitle')}>{t('task_table_action_alert_subtitle')}</Typography>
        </Stack>

        <Stack className={cx('viewHeader')}>
          <Typography className={cx('title')}>{t('order_detail_title_costing_overview')}</Typography>
        </Stack>

        <Stack direction='row' alignItems='center' gap={6} justifyContent={'space-between'} width={'33%'}>
          {dataOverview && dataOverview.map((item) => <JobCostingItem data={item} />)}
        </Stack>

        <Stack direction={'column'} className={cx('viewCostBreakdown')}>
          <Stack p={4}>
            <Typography className={cx('title')}>{t('order_detail_title_cost_breakdown')}</Typography>
          </Stack>

          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            className={cx('viewHeaderCost')}
          >
            <Typography className={cx('titleCost')}>{t('order_detail_title_category')}</Typography>
            <Typography className={cx('titleCost')}>{t('order_detail_title_amount')}</Typography>
          </Stack>

          <Stack>
            <Accordion defaultExpanded>
              <AccordionSummary
                className={cx('summaryButton')}
                expandIcon={<img src={icons.commonIconArrowBottom} alt={t('common_alt_img', { ns: 'common/base' })} />}
              >
                <Typography className={cx('subtitleCost')}>{t('order_detail_title_order_revenue')}</Typography>
                <Typography className={cx('subValueCost')}>
                  {formatCurrencyUSD({ value: orderCostingDetail?.revenue?.totalAmount, showDecimal: true })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={cx('detailsCost')}>
                <Stack spacing={3} className={cx('detailsCostList')}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_rental_item_total')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.revenue?.totalItems, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_delivery_pickup_total')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.revenue?.totalDelivery, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_damage_waiver')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.revenue?.damageWaiver, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_labor_fees')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.revenue?.laborFee, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_tax_sub')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.revenue?.tax, showDecimal: true })}
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary
                className={cx('summaryButton')}
                expandIcon={<img src={icons.commonIconArrowBottom} alt={t('common_alt_img', { ns: 'common/base' })} />}
              >
                <Typography className={cx('subtitleCost')}>{t('order_detail_title_order_expenses')}</Typography>
                <Typography className={cx('subValueCostExp')}>
                  {formatCurrencyUSD({ value: orderCostingDetail?.expenses?.totalAmount, showDecimal: true })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={cx('detailsCost')}>
                <Stack spacing={3} className={cx('detailsCostList')}>
                  <Typography className={cx('subLabelCost')}>{t('order_detail_title_direct_labor_inputs')}</Typography>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_loading')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.expenses?.loadingAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_delivery')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.expenses?.deliveryAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_pickup')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.expenses?.pickupAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography className={cx('labelCost')}>{t('order_detail_title_unloading')}</Typography>
                    <Typography className={cx('valueCost')}>
                      {formatCurrencyUSD({ value: orderCostingDetail?.expenses?.unloadingAmount, showDecimal: true })}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack className={cx('detailsDirectCost')}>
                  <Typography className={cx('subLabelCost')}>
                    {t('order_detail_title_direct_labor_subtotal')}
                  </Typography>
                  <Typography mt={2} className={cx('subLabelCost')}>
                    {t('order_detail_title_other_expenses')}
                  </Typography>
                  <Stack spacing={3}>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography className={cx('labelCost')}>{t('order_detail_title_additional_labor')}</Typography>
                      <Typography className={cx('valueCost')}>
                        {formatCurrencyUSD({
                          value: orderCostingDetail?.expenses?.additionalLaborAmount,
                          showDecimal: true,
                        })}
                      </Typography>
                    </Stack>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography className={cx('labelCost')}>{t('order_detail_title_mileage')}</Typography>
                      <Typography className={cx('valueCost')}>
                        {formatCurrencyUSD({ value: orderCostingDetail?.expenses?.mileageAmount, showDecimal: true })}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Typography mt={2} className={cx('subLabelCost')}>
                    {t('order_detail_title_additional_expenses')}
                  </Typography>

                  <Stack direction={'column'} spacing={3} width={'100%'}>
                    {additionalExpensesFields.map((field, index) => (
                      <Grid2 key={index} spacing={4} container>
                        <Grid2 size={9}>
                          <Controller
                            key={`${field.id}-${index}-description`}
                            control={control}
                            name={`additionalExpenses.${index}.description`}
                            render={({ field }) => (
                              <BaseInput
                                disabled
                                placeholder={t('order_detail_title_expense_description')}
                                value={field.value}
                                onChange={field.onChange}
                                errorMessage={errors?.additionalExpenses?.[index]?.description?.message}
                              />
                            )}
                          />
                        </Grid2>

                        <Grid2 className={cx('viewPrice')} size={3}>
                          <Typography className={cx('labelCost')} mt={'14px'}>
                            $
                          </Typography>

                          <Stack direction={'column'} width={'100%'}>
                            <Controller
                              key={`${field.id}-${index}-amount`}
                              control={control}
                              name={`additionalExpenses.${index}.amount`}
                              render={({ field }) => (
                                <BaseInput
                                  disabled
                                  placeholder={t('common_text_placeholder_currency', {
                                    ns: 'common/base',
                                  })}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(formatInputCurrency(event.target.value));
                                  }}
                                  errorMessage={errors?.additionalExpenses?.[index]?.amount?.message}
                                />
                              )}
                            />
                          </Stack>
                        </Grid2>

                        {/* <Grid2 size={0.5} className={cx('viewRemove')}>
                          <IconButton
                            key={`${field.id}-${index}-remove`}
                            className={cx('buttonRemove')}
                            onClick={() => handleRemoveAdditionalExpense(index, field.idDetail)}
                          >
                            <img src={icons.commonIconTrash} alt={t('common_alt_img')} />
                          </IconButton>
                        </Grid2> */}
                      </Grid2>
                    ))}
                  </Stack>

                  {/* <Typography component={'span'} className={cx('buttonAdd')} onClick={handleAddAdditionalExpense}>
                    {t('order_detail_title_add_additional_expense')}
                  </Typography> */}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
          <Stack className={cx('viewTotalCost')}>
            <Typography className={cx('totalCostTitle')}>{t('order_detail_title_total_profit')}</Typography>
            <Typography
              className={cx('totalCostValue', Number(orderCostingDetail?.totalProfit) < 0 && 'totalCostValueError')}
            >
              {formatCurrencyUSD({ value: orderCostingDetail?.totalProfit, showDecimal: true })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Button type='submit' variant='contained' className={cx('buttonAction')}>
        {t('common_action_save', { ns: 'common/base' })}
      </Button>

      {isLoading && <Loading />}
    </Box>
  );
};

export default OrderCostingTab;
